import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";

// Terminal states across both migration data models this app has (PC/Server
// migrations via jobs.py's generic /jobs, Data Migration via its own
// /storage-migrations) -- confirmed against both routers' actual status
// literals rather than assumed, since the two systems don't share a status
// enum. Anything not in this set counts as "active" for the tray badge.
const TERMINAL_STATUSES = new Set([
    "completed", "completed_with_errors", "completed_unverified",
    "failed", "cancelled", "canceled",
]);

const POLL_MS = 8000; // matches Dashboard.jsx's own polling cadence

/**
 * DesktopNotificationBridge — renders nothing. Mounted once inside the
 * authenticated app shell (Layout.jsx), only when running in Electron.
 *
 * Polls the same endpoints Dashboard/Migrations pages already call
 * (no new backend behavior) and diffs each job's status against what it
 * was on the previous poll. The first time a job is *seen* already
 * terminal it's just recorded, not notified about -- only a genuine
 * transition (was active, now terminal) fires a native notification, so
 * reopening the app after a migration finished overnight doesn't spam
 * notifications for everything that happened while it was closed.
 *
 * Also keeps the tray's "N Running" count live and wires notification
 * clicks back to the right migration's detail page.
 */
export default function DesktopNotificationBridge() {
    const navigate = useNavigate();
    const knownStatus = useRef(new Map()); // job id -> last seen status
    const primed = useRef(false); // true once the first poll has recorded baseline state

    useEffect(() => {
        if (!window.moovbyte) return;

        const unsubscribe = window.moovbyte.onNotificationClick(({ migrationId }) => {
            if (!migrationId) return;
            const { kind, id } = migrationId;
            if (kind === "storage") navigate(`/migrate/storage/${id}`);
            else if (kind === "job") navigate(`/jobs/${id}`);
        });

        let cancelled = false;

        const poll = async () => {
            try {
                const [jobsRes, storageRes] = await Promise.all([
                    http.get("/jobs").catch(() => ({ data: [] })),
                    http.get("/storage-migrations").catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;

                const items = [
                    ...jobsRes.data.map((j) => ({ id: j.id, kind: "job", status: j.status, name: j.name })),
                    ...storageRes.data.map((j) => ({ id: j.id, kind: "storage", status: j.status, name: j.name })),
                ];

                let activeCount = 0;
                for (const item of items) {
                    const isTerminal = TERMINAL_STATUSES.has(item.status);
                    if (!isTerminal) activeCount += 1;

                    const key = `${item.kind}:${item.id}`;
                    const prevStatus = knownStatus.current.get(key);
                    knownStatus.current.set(key, item.status);

                    // Only notify on a real transition seen after the baseline poll,
                    // and only when it just BECAME terminal (prevStatus existed and
                    // wasn't already terminal itself).
                    if (primed.current && prevStatus && prevStatus !== item.status && isTerminal && !TERMINAL_STATUSES.has(prevStatus)) {
                        fireCompletionNotification(item);
                    }
                }

                primed.current = true;
                window.moovbyte.updateTrayStatus(activeCount);
            } catch {
                // Best-effort background poller -- a failed poll just tries again
                // next tick, no need to surface it as an app-level error.
            }
        };

        poll();
        const iv = setInterval(poll, POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(iv);
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- navigate is stable from react-router
    }, []);

    return null;
}

function fireCompletionNotification({ id, kind, status, name }) {
    const label = name || (kind === "storage" ? "Data migration" : "Migration");
    const failed = status === "failed" || status === "completed_with_errors";
    window.moovbyte.showNotification({
        title: failed ? "Migration needs attention" : "Migration completed",
        body: failed
            ? `${label} ${status === "failed" ? "failed" : "completed with errors"}.`
            : `${label} completed successfully.`,
        migrationId: { kind, id },
    });
}
